// Lines up this element's top edge with a target element in a sibling column
// (product page: the "After you order" rule level with the "Order total" rule).
// Whichever of the two sits higher gets a top margin, so the lower one never has to move up.
// Offsets are measured inside each column, so a sticky neighbour column doesn't skew the result.
// Only applies from the `data-min-width` breakpoint up; below it both margins are cleared.
class SAlignRule extends HTMLElement {
  connectedCallback() {
    this.frame = 0;
    this.schedule = this.schedule.bind(this);
    this.grid = this.parentElement?.parentElement || null;
    if (!this.grid) return;

    this.resizeObserver = new ResizeObserver(this.schedule);
    this.resizeObserver.observe(this.grid);
    Array.from(this.grid.children).forEach((column) => this.resizeObserver.observe(column));

    // Variant changes swap the product form partial, which replaces the target element.
    this.mutationObserver = new MutationObserver(this.schedule);
    this.mutationObserver.observe(this.grid, { childList: true, subtree: true });

    this.onResize = () => { this.lastResize = performance.now(); };
    window.addEventListener('resize', this.onResize);
    window.addEventListener('load', this.schedule, { once: true });
    document.fonts?.ready.then(this.schedule);
    this.grid.addEventListener('load', this.schedule, true);
    this.lateTimers = [setTimeout(this.schedule, 400), setTimeout(this.schedule, 1500)];
    this.schedule();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    window.removeEventListener('load', this.schedule);
    window.removeEventListener('resize', this.onResize);
    this.grid?.removeEventListener('load', this.schedule, true);
    (this.lateTimers || []).forEach(clearTimeout);
    cancelAnimationFrame(this.frame);
    clearTimeout(this.glideTimer);
  }

  schedule() {
    // While a glide is running, its own layout changes would re-trigger a measure and cut it short.
    if (this.gliding) return;
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.update());
  }

  update() {
    const previousMargin = this.style.marginTop;
    const previousTransition = this.style.transition;
    this.style.transition = 'none';
    this.measure();
    this.glide(previousMargin, previousTransition);
  }

  // Big position changes (the alignment switching on or off as the window crosses the breakpoint)
  // glide over 300ms instead of snapping; small ones during a drag-resize follow instantly.
  glide(previousMargin, previousTransition) {
    const finalMargin = this.style.marginTop;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const distance = Math.abs((parseFloat(finalMargin) || 0) - (parseFloat(previousMargin) || 0));
    // Only glide while the shopper is resizing the window; page load and variant changes stay instant.
    const resizing = performance.now() - (this.lastResize || -Infinity) < 500;
    if (reduceMotion || !resizing || distance < 24) {
      this.style.transition = previousTransition;
      return;
    }
    this.style.marginTop = previousMargin;
    void this.offsetHeight;
    this.style.transition = 'margin-top 300ms cubic-bezier(0.22, 1, 0.36, 1)';
    this.style.marginTop = finalMargin;
    this.gliding = true;
    clearTimeout(this.glideTimer);
    this.glideTimer = setTimeout(() => {
      this.gliding = false;
      this.style.transition = previousTransition;
      this.schedule();
    }, 320);
  }

  measure() {
    const minWidth = this.dataset.minWidth || '768px';
    const target = this.grid?.querySelector(this.dataset.target || '');
    const ownColumn = this.parentElement;
    const targetColumn = target ? Array.from(this.grid.children).find((child) => child.contains(target)) : null;

    this.style.marginTop = '';
    if (target instanceof HTMLElement) target.style.marginTop = '';
    if (!(target instanceof HTMLElement) || !ownColumn || !targetColumn) return;
    if (!window.matchMedia(`(min-width: ${minWidth})`).matches) return;

    // Measure both rules against the grid with the sticky column briefly unstuck, so the
    // comparison uses where each rule sits in the layout, not where scrolling has moved it.
    // Re-measure after each adjustment and correct any remaining gap (up to six passes).
    // All of this runs in one frame, so nothing is painted in between.
    const savedPosition = targetColumn.style.position;
    targetColumn.style.position = 'static';
    const gridTop = () => this.grid.getBoundingClientRect().top;
    // If the page is rendered scaled (CSS zoom or a scaled preview), measured pixels differ from
    // the pixels a margin moves by; convert using the element's measured vs layout height.
    const rectHeight = this.getBoundingClientRect().height;
    const scale = rectHeight > 0 && this.offsetHeight > 0 ? rectHeight / this.offsetHeight : 1;
    const top = (el) => (el.getBoundingClientRect().top - gridTop()) / scale;
    let ownMargin = 0;
    let targetMargin = 0;
    for (let pass = 0; pass < 6; pass += 1) {
      const diff = Math.round(top(target) - top(this));
      if (Math.abs(diff) < 1) break;
      // data-own-only: only this element moves down; the target never gets a margin.
      if (this.hasAttribute('data-own-only')) {
        ownMargin = Math.max(0, ownMargin + diff);
        this.style.marginTop = ownMargin ? `${ownMargin}px` : '';
        continue;
      }
      if (diff > 0) {
        if (targetMargin > 0) targetMargin = Math.max(0, targetMargin - diff);
        else ownMargin += diff;
      } else if (ownMargin > 0) {
        ownMargin = Math.max(0, ownMargin + diff);
      } else {
        targetMargin -= diff;
      }
      this.style.marginTop = ownMargin ? `${ownMargin}px` : '';
      target.style.marginTop = targetMargin ? `${targetMargin}px` : '';
    }
    targetColumn.style.position = savedPosition;
  }
}

if (!customElements.get('s-align-rule')) customElements.define('s-align-rule', SAlignRule);
